import { useCallback, useState } from 'react';
import { Alert, Box, Button, Center, Stack, Text, Title } from '@mantine/core';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '../../api/client';
import { checkParentPin } from '../../api/gen';
import { Keypad } from '../../components/Keypad';
import { useOrg, useSession } from '../../lib/session';
import { SignInPage } from '../SignInPage';

// What stands between a child and the dashboard.
//
// The PIN does not create a session — it re-authenticates a parent who is
// already signed in. A PIN that could sign somebody in would be a four-digit
// password on a device children use, which is why `check-parent-pin` answers
// 204 and nothing else: it grants no credential, it only confirms.
export function ParentGate() {
  const { hasParent, parentMode, enterParentMode } = useSession();
  const org = useOrg();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (value: string) => {
      if (busy) return;
      setBusy(true);
      setError('');
      try {
        await unwrap(checkParentPin({ headers: org, body: { pin: value } }));
        enterParentMode();
      } catch (err) {
        setPin('');
        setError(
          err instanceof ApiError && err.status === 409
            ? 'Für diese Familie ist noch keine Eltern-PIN gesetzt.'
            : 'Das war nicht die richtige PIN.',
        );
      } finally {
        setBusy(false);
      }
    },
    [org, enterParentMode, busy],
  );

  // No parent credential in this tab: sign in first. The child flow never
  // needed one, which is why this is the only place it comes up.
  if (!hasParent) return <SignInPage />;
  if (parentMode) return <Outlet />;

  return (
    <Center mih="100vh" p="lg">
      <Stack align="center" w={320} maw="100%" gap="lg">
        <Box ta="center">
          <Title order={3}>Eltern-Modus</Title>
          <Text c="dimmed" size="sm">
            Gib deine Eltern-PIN ein
          </Text>
        </Box>

        {error ? (
          <Alert color="red" variant="light" w="100%">
            {error}
          </Alert>
        ) : null}

        <Keypad pin={pin} onChange={setPin} onComplete={submit} color="ink" disabled={busy} />

        <Button variant="subtle" color="gray" onClick={() => navigate('/')}>
          Zurück
        </Button>
      </Stack>
    </Center>
  );
}

/** Used by App's route table; keeps the redirect in one place. */
export function ParentRedirect() {
  return <Navigate to="/eltern" replace />;
}
